/**
 * editorSqlTokens — the pure half of `useEditorSqlTokens`: split SQL into
 * lines (keeping separators), tokenize with a Monaco-shaped tokenizer, and
 * colour each token by the longest dot-prefix rule, the way Monaco themes
 * match. No Monaco import, so tests and fakes can use it directly.
 */
import type { MonacoTokenRule } from '../../../../core/theme/monacoColorSchemes';

export type EditorSqlToken = {
  readonly text: string;
  /** First segment of the Monaco token type (`keyword`, `string`, …); '' for line breaks. */
  readonly kind: string;
  /** `#rrggbb`, or undefined to inherit the surrounding text colour. */
  readonly color?: string;
};

function colorFor(type: string, rules: ReadonlyMap<string, string>): string | undefined {
  let key = type;
  for (;;) {
    const hit = rules.get(key);
    if (hit) return `#${hit}`;
    const dot = key.lastIndexOf('.');
    if (dot < 0) return undefined;
    key = key.slice(0, dot);
  }
}

/** One Monaco token: where it starts on its line, and its type (`keyword.sql`). */
export type MonacoLineToken = { readonly offset: number; readonly type: string };

/**
 * Pure core: split `sql` into lines (keeping the separators), tokenize with
 * the given tokenizer, and colour each token from `rules`. Exported for tests.
 */
export function buildEditorSqlTokens(
  sql: string,
  tokenize: (text: string) => ReadonlyArray<ReadonlyArray<MonacoLineToken>>,
  rules: ReadonlyArray<MonacoTokenRule>,
): EditorSqlToken[] {
  const ruleMap = new Map(
    rules.filter((rule) => rule.foreground).map((rule) => [rule.token, rule.foreground as string]),
  );
  // Monaco splits lines on \r\n, \r and \n; keep the separators so the
  // segments reproduce the input exactly.
  const parts = sql.split(/(\r\n|\r|\n)/);
  const lines = parts.filter((_, i) => i % 2 === 0);
  const tokenLines = tokenize(lines.join('\n'));
  const out: EditorSqlToken[] = [];
  lines.forEach((line, i) => {
    const tokens = tokenLines[i] ?? [];
    // Anything Monaco left untokenized (whole line, or before the first
    // token) is kept as plain text so nothing is dropped.
    const firstOffset = tokens.length > 0 ? tokens[0].offset : line.length;
    if (firstOffset > 0) out.push({ text: line.slice(0, firstOffset), kind: '' });
    tokens.forEach((token, j) => {
      const end = j + 1 < tokens.length ? tokens[j + 1].offset : line.length;
      const text = line.slice(token.offset, end);
      if (!text) return;
      out.push({ text, kind: token.type.split('.')[0], color: colorFor(token.type, ruleMap) });
    });
    if (i < lines.length - 1) out.push({ text: parts[i * 2 + 1], kind: '' });
  });
  return out;
}
